/**
 * /apps/:appId/org-chart
 *
 * Tab 1 — "Departments": Branch → Dept tree; clicking a dept opens a roles modal.
 * Tab 2 — "Role Hierarchy": All roles nested by reports_to_role_id.
 * Tab 3 — "Employee Hierarchy": Employees nested by manager_employee_id.
 *   Clicking an employee card opens a sidebar showing their direct reports.
 *   Direct reports are draggable — drop onto any employee card to reassign manager.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../../components/Modal';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatRupeesShort, formatRupeesFull } from '../../lib/format';

// ── Circled arrow icon (transparent bg, outlined circle + arrow) ────────────
function CircleArrowIcon({ dir }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.25" stroke="currentColor" strokeWidth="1.25" />
      {dir < 0 ? (
        <path d="M14.5 8L10 12l4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M9.5 8L14 12l-4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

// ── Horizontal scroll container with prev/next arrow buttons ────────────────
function HScrollWithArrows({ children, className, style, wrapStyle }) {
  const ref = useRef(null);
  const [canLeft, setCanLeft]   = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll]);

  const doScroll = (dir) => ref.current?.scrollBy({ left: dir * 340, behavior: 'smooth' });

  const arrowBtn = (dir, visible) => (
    <button
      onClick={() => doScroll(dir)}
      style={{
        flexShrink: 0,
        border: 'none', background: 'transparent',
        padding: '4px 10px',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-secondary)',
        alignSelf: 'center',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.15s',
      }}
    ><CircleArrowIcon dir={dir} /></button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', ...wrapStyle }}>
      {arrowBtn(-1, canLeft)}
      <div ref={ref} className={className} style={{ flex: 1, minWidth: 0, ...style, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {children}
      </div>
      {arrowBtn(1, canRight)}
    </div>
  );
}

// Long figures in lakh/crore (₹2 L, ₹1.2 Cr); the exact amount shows on hover.
const fmtCurrency = formatRupeesShort;
const fullCurrency = formatRupeesFull;

function initials(name = '') {
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

// ── Page shell ──────────────────────────────────────────────────────────────

export default function OrgChart({ hideCTC = false }) {
  const { appId } = useParams();
  const api = useApi();
  const [tree, setTree]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [view, setView]       = useState('departments');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getOrgChart(appId);
        if (cancelled) return;
        setTree(res);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, appId]);

  if (loading) return <p className="muted">Building org chart…</p>;
  if (error)   return <div className="banner banner-error">{error}</div>;
  if (!tree)   return null;

  const c = tree.counts;
  const totalCTC = !hideCTC && fmtCurrency(tree.org_total_ctc);

  const refresh = async () => {
    try {
      const res = await api.getOrgChart(appId);
      setTree(res);
    } catch (_) {}
  };

  return (
    <section className="entity-section">
      <div className="org-chart-header">
        <div>
          <h1 className="page-title">Org Chart</h1>
          <p className="muted" style={{ marginBottom: 0 }}>
            {c.departments} dept(s) · {c.roles} role(s) · {c.employees} employee(s)
            {totalCTC && <> · <strong title={fullCurrency(tree.org_total_ctc)}>Total CTC: {totalCTC}</strong></>}
          </p>
        </div>
        <div className="org-view-toggle">
          <button
            className={`org-view-btn${view === 'departments' ? ' active' : ''}`}
            onClick={() => setView('departments')}
          >
            Departments
          </button>
          <button
            className={`org-view-btn${view === 'roles' ? ' active' : ''}`}
            onClick={() => setView('roles')}
          >
            Role Hierarchy
          </button>
          <button
            className={`org-view-btn${view === 'employees' ? ' active' : ''}`}
            onClick={() => setView('employees')}
          >
            Employee Hierarchy
          </button>
        </div>
      </div>

      {view === 'departments' ? (
        <DeptView tree={tree} hideCTC={hideCTC} />
      ) : view === 'roles' ? (
        <RoleView tree={tree} hideCTC={hideCTC} />
      ) : (
        <EmployeeView tree={tree} appId={appId} api={api} onRefresh={refresh} />
      )}
    </section>
  );
}

// ── Collect all root-level depts out of the branch tree ─────────────────────

function collectAllRootDepts(branches) {
  const result = [];
  function walk(branch) {
    for (const dept of (branch.departments || [])) result.push(dept);
    for (const child of (branch.children   || [])) walk(child);
  }
  for (const b of branches) walk(b);
  return result;
}

// ── Tab 1: Department tree + roles modal ────────────────────────────────────

function DeptView({ tree, hideCTC }) {
  const [modalDept, setModalDept] = useState(null);

  const allTopDepts = [
    ...collectAllRootDepts(tree.branches || []),
    ...(tree.orphan_departments || []),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const hasOrphans = (tree.unassigned_employees?.length ?? 0) > 0;

  if (allTopDepts.length === 0 && !hasOrphans) {
    return (
      <div className="empty-state-small">
        No departments yet. Add departments to start building your org structure.
      </div>
    );
  }

  return (
    <>
      {allTopDepts.length > 0 && (
        <HScrollWithArrows className="oc-wrap">
          <div className="oc-root-row">
            {allTopDepts.map((d) => (
              <DeptNode key={d.id} dept={d} onViewRoles={setModalDept} hideCTC={hideCTC} />
            ))}
          </div>
        </HScrollWithArrows>
      )}

      {hasOrphans && (
        <div className="org-orphans">
          <h3 style={{ color: '#856404', marginTop: 0, marginBottom: 10 }}>Unassigned</h3>
          <OrphanList
            title="Employees without a role"
            items={tree.unassigned_employees.map((e) => e.full_name)}
          />
        </div>
      )}

      <DeptRolesModal
        dept={modalDept}
        onClose={() => setModalDept(null)}
        hideCTC={hideCTC}
      />
    </>
  );
}

// ── Department node ─────────────────────────────────────────────────────────

function DeptNode({ dept, onViewRoles, hideCTC }) {
  const [expanded, setExpanded] = useState(true);
  const subDepts    = dept.children || [];
  const roles       = dept.roles    || [];
  const hasSubDepts = subDepts.length > 0;
  const hasRoles    = roles.length > 0;
  const costStr     = !hideCTC && fmtCurrency(dept.total_ctc);

  return (
    <div className="oc-group">
      <div
        className={`oc-box oc-box-dept${hasRoles ? ' oc-box-has-roles' : ''}`}
        onClick={hasRoles ? () => onViewRoles(dept) : undefined}
        title={hasRoles ? `Click to view roles in ${dept.name}` : undefined}
      >
        {hasSubDepts && (
          <button
            className="oc-toggle"
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            title={expanded ? 'Collapse sub-departments' : 'Expand sub-departments'}
          >
            {expanded ? '−' : '+'}
          </button>
        )}

        <div className="oc-tag-row">
          <span className="oc-tag oc-tag-dept">Dept</span>
        </div>
        <div className="oc-title">{dept.name}</div>
        {dept.code && <div className="oc-code">{dept.code}</div>}
        {dept.head_role_title && (
          <div className="oc-box-head-role">Head: <em>{dept.head_role_title}</em></div>
        )}
        {costStr && <div className="oc-cost-badge" title={fullCurrency(dept.total_ctc)}>{costStr}</div>}

        {hasRoles && (
          <div className="oc-roles-badge">
            {dept.total_role_count || roles.length} role{(dept.total_role_count || roles.length) !== 1 ? 's' : ''} →
          </div>
        )}
      </div>

      {expanded && hasSubDepts && (
        <>
          <div className="oc-connector-down" />
          <div className="oc-level">
            {subDepts.map((c) => (
              <DeptNode key={c.id} dept={c} onViewRoles={onViewRoles} hideCTC={hideCTC} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Dept roles modal ────────────────────────────────────────────────────────

function DeptRolesModal({ dept, onClose, hideCTC }) {
  const roles = dept?.roles || [];

  return (
    <Modal
      open={!!dept}
      title={dept ? `${dept.name} — Roles` : ''}
      onClose={onClose}
      size="xl"
    >
      {dept && (
        <div className="oc-modal-content">
          <div className="oc-modal-meta">
            {dept.code && <span className="oc-code">{dept.code}</span>}
            {dept.head_role_title && (
              <span className="oc-box-head-role">
                Head: <em>{dept.head_role_title}</em>
              </span>
            )}
            {!hideCTC && dept.total_ctc > 0 && (
              <span className="oc-cost-badge" title={fullCurrency(dept.total_ctc)}>{fmtCurrency(dept.total_ctc)}</span>
            )}
          </div>

          {roles.length === 0 ? (
            <p className="muted" style={{ marginTop: 16 }}>
              No roles assigned to this department yet.
            </p>
          ) : (
            <HScrollWithArrows className="oc-wrap" style={{ marginTop: 20 }}>
              <div className="oc-root-row">
                {roles.map((r) => <RoleNode key={r.id} role={r} hideCTC={hideCTC} />)}
              </div>
            </HScrollWithArrows>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Role node (top-to-bottom subtree, starts expanded) ──────────────────────

function RoleNode({ role, showSubtree = false, hideCTC = false }) {
  const [expanded, setExpanded] = useState(true);
  const [showEmps, setShowEmps] = useState(false);
  const subRoles    = role.children || [];
  const employees   = role.employees || [];
  const platformMembers = role.platform_members || [];
  // Merge HR employees + platform users, deduplicate by name
  const allMembers = [
    ...employees.map(e => ({ id: `e_${e.id}`, full_name: e.full_name })),
    ...platformMembers,
  ];
  const hasChildren = subRoles.length > 0;
  const costStr     = !hideCTC && fmtCurrency(role.max_yearly_ctc);
  const subtreeStr  =
    !hideCTC && showSubtree && role.subtree_ctc > (role.max_yearly_ctc || 0)
      ? fmtCurrency(role.subtree_ctc)
      : null;

  const metaParts = [
    role.code,
    role.level != null && role.level > 0 ? `L${role.level}` : null,
  ].filter(Boolean);

  return (
    <div className="oc-group">
      <div className={`oc-box oc-box-role${showEmps ? ' oc-box-role-expanded' : ''}`}>
        {hasChildren && (
          <button
            className="oc-toggle"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '−' : '+'}
          </button>
        )}
        <div className="oc-tag-row"><span className="oc-tag oc-tag-role">Role</span></div>
        <div className="oc-title">{role.title}</div>
        {metaParts.length > 0 && <div className="oc-code">{metaParts.join(' · ')}</div>}
        {costStr && <div className="oc-cost-badge" title={fullCurrency(role.max_yearly_ctc)}>{costStr}</div>}
        {subtreeStr && (
          <div className="oc-cost-badge oc-cost-subtree" title={fullCurrency(role.subtree_ctc)}>subtree: {subtreeStr}</div>
        )}
        {allMembers.length > 0 && (
          <>
            <div
              className="oc-emp-count-badge"
              onClick={(e) => { e.stopPropagation(); setShowEmps((v) => !v); }}
              title={showEmps ? 'Hide members' : 'Show members'}
            >
              {allMembers.length} member{allMembers.length !== 1 ? 's' : ''} {showEmps ? '▲' : '▼'}
            </div>
            {showEmps && (
              <div className="oc-emp-name-list">
                {allMembers.map((e) => (
                  <div key={e.id} className="oc-emp-name-item">
                    <div className="oc-emp-report-avatar" style={{ flexShrink: 0 }}>
                      {e.photo_url
                        ? <img src={e.photo_url} alt={e.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        : initials(e.full_name)}
                    </div>
                    <span className="oc-emp-name-item-text" title={e.full_name}>{e.full_name}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {expanded && hasChildren && (
        <>
          <div className="oc-connector-down" />
          <div className="oc-level">
            {subRoles.map((c) => (
              <RoleNode key={c.id} role={c} showSubtree={showSubtree} hideCTC={hideCTC} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 2: Role hierarchy ────────────────────────────────────────────────────

function RoleView({ tree, hideCTC }) {
  if (!tree.role_tree?.length) {
    return <div className="empty-state-small">No roles yet.</div>;
  }
  return (
    <HScrollWithArrows className="oc-wrap">
      <div className="oc-root-row">
        {tree.role_tree.map((r) => (
          <RoleNode key={r.id} role={r} showSubtree hideCTC={hideCTC} />
        ))}
      </div>
    </HScrollWithArrows>
  );
}

// ── Tab 3: Employee Hierarchy ────────────────────────────────────────────────

const ZOOM_STEP = 0.1;
const ZOOM_MIN  = 0.4;
const ZOOM_MAX  = 1.5;
const ZOOM_DEFAULT = 0.85;

function EmployeeView({ tree, appId, api, onRefresh }) {
  const { myRole } = useWorkspace();
  const canDrag = myRole === 'super_admin' || myRole === 'admin';
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [selectedNodeKey, setSelectedNodeKey] = useState(null);
  const draggedIdRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [zoom, setZoom] = useState(ZOOM_DEFAULT);
  const [addReportFor, setAddReportFor] = useState(null);
  const [hierarchyAlert, setHierarchyAlert] = useState(null);

  const scrollRef = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll, tree]);

  const doScroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 340, behavior: 'smooth' });

  const arrowBtn = (dir, visible) => (
    <button
      onClick={() => doScroll(dir)}
      style={{
        flexShrink: 0,
        border: 'none', background: 'transparent',
        padding: '4px 10px',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-secondary)',
        alignSelf: 'center',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.15s',
      }}
    ><CircleArrowIcon dir={dir} /></button>
  );

  const empRoots = tree.employee_tree || [];

  const handleDragStart = (empId, roleId) => {
    draggedIdRef.current = { id: empId, role_id: roleId };
  };

  // roleId scopes the move to just that one role a person holds — a person
  // with multiple roles appears as multiple cards, and only the card that
  // was actually dragged/picked should move, not every role that person has.
  // managerRoleId records which of the *manager's* role cards was the drop
  // target/picked entry, so a manager with multiple roles gets the reportee
  // nested under the correct card instead of an arbitrary one of theirs.
  const assignManager = async (platformUserId, managerPlatformUserId, roleId, managerRoleId) => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.setEmployeeManager(appId, {
        platform_user_id: platformUserId,
        manager_platform_user_id: managerPlatformUserId,
        role_id: roleId,
        manager_role_id: managerRoleId,
      });
      setSelectedEmp(null);
      setSelectedNodeKey(null);
      await onRefresh();
      return true;
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to reassign manager';
      if (err.response?.data?.code === 'role_hierarchy') {
        setHierarchyAlert(msg);
      } else {
        setSaveError(msg);
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = async (targetEmpId, targetRoleId) => {
    const dragged = draggedIdRef.current;
    draggedIdRef.current = null;
    if (!dragged || dragged.id === targetEmpId) return;
    await assignManager(dragged.id, targetEmpId, dragged.role_id, targetRoleId);
  };

  if (empRoots.length === 0) {
    return <div className="empty-state-small">No employees yet.</div>;
  }

  return (
    <div>
      {/* Zoom controls */}
      <div className="oc-emp-zoom-bar">
        <button
          className="oc-emp-zoom-btn"
          onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
          disabled={zoom <= ZOOM_MIN}
          title="Zoom out"
        >−</button>
        <span className="oc-emp-zoom-label">{Math.round(zoom * 100)}%</span>
        <button
          className="oc-emp-zoom-btn"
          onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
          disabled={zoom >= ZOOM_MAX}
          title="Zoom in"
        >+</button>
        <button
          className="oc-emp-zoom-btn oc-emp-zoom-reset"
          onClick={() => setZoom(ZOOM_DEFAULT)}
          title="Reset zoom"
        >Reset</button>
      </div>

      {saveError && (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>{saveError}</div>
      )}
      {saving && (
        <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>Saving…</div>
      )}

      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {arrowBtn(-1, canLeft)}
        <div className="oc-emp-layout" style={{ flex: 1, minWidth: 0 }}>
          {/* Main tree */}
          <div
            ref={scrollRef}
            className="oc-emp-tree-area"
            style={{ flex: 1, minWidth: 0, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onScroll={checkScroll}
          >
            <div style={{ zoom }}>
              <div className="oc-wrap oc-wrap-no-scroll">
                <div className="oc-root-row">
                  {empRoots.map((e) => (
                    <EmployeeNode
                      key={e.node_key || e.id}
                      emp={e}
                      onSelect={(emp) => { setSelectedEmp(emp); setSelectedNodeKey(emp.node_key || emp.id); }}
                      selectedId={selectedNodeKey}
                      onDragStart={canDrag ? handleDragStart : null}
                      onDrop={canDrag ? handleDrop : null}
                      canDrag={canDrag}
                      onAddReport={canDrag ? setAddReportFor : null}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          {selectedEmp && (
            <EmployeeSidebar
              emp={selectedEmp}
              onClose={() => { setSelectedEmp(null); setSelectedNodeKey(null); }}
              onDragStart={canDrag ? handleDragStart : null}
              canDrag={canDrag}
            />
          )}
        </div>
        {arrowBtn(1, canRight)}
      </div>

      {addReportFor && (
        <AddReportModal
          manager={addReportFor}
          empRoots={empRoots}
          onAssign={async (userId, roleId) => {
            const ok = await assignManager(userId, addReportFor.id, roleId, addReportFor.role_id);
            if (ok) setAddReportFor(null);
          }}
          onClose={() => setAddReportFor(null)}
        />
      )}

      {hierarchyAlert && (
        <Modal
          open
          title="Not allowed"
          onClose={() => setHierarchyAlert(null)}
          size="sm"
          footer={
            <div className="form-actions">
              <button className="btn-primary" onClick={() => setHierarchyAlert(null)}>OK</button>
            </div>
          }
        >
          <p style={{ margin: 0 }}>{hierarchyAlert}</p>
        </Modal>
      )}
    </div>
  );
}

// ── Employee tree node ───────────────────────────────────────────────────────

function EmployeeNode({ emp, onSelect, selectedId, onDragStart, onDrop, canDrag, onAddReport }) {
  const [expanded, setExpanded] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const direct = emp.direct_reports || [];
  const nodeKey = emp.node_key || emp.id;
  const isSelected = selectedId === nodeKey;
  const otherRoles = emp.multi_role
    ? (emp.all_roles || []).filter(r => r.role_id !== emp.role_id)
    : [];

  return (
    <div className="oc-group">
      <div
        className={`oc-box oc-box-emp${isSelected ? ' oc-box-emp-selected' : ''}${isDragOver && canDrag ? ' oc-box-emp-drop' : ''}`}
        onClick={() => onSelect({ ...emp, node_key: nodeKey })}
        draggable={canDrag}
        onDragStart={canDrag ? (e) => { e.stopPropagation(); onDragStart(emp.id, emp.role_id); } : undefined}
        onDragOver={canDrag ? (e) => { e.preventDefault(); setIsDragOver(true); } : undefined}
        onDragLeave={canDrag ? () => setIsDragOver(false) : undefined}
        onDrop={canDrag ? (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); onDrop(emp.id, emp.role_id); } : undefined}
        title={canDrag ? 'Drag to reassign manager · drop a card here to make them report to this person' : undefined}
      >
        {canDrag && onAddReport && (
          <button
            className="oc-add-btn"
            onClick={(e) => { e.stopPropagation(); onAddReport(emp); }}
            title="Add report"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="2"/>
              <path d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M19 8v6M16 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        )}
        {direct.length > 0 && (
          <button
            className="oc-toggle"
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '−' : '+'}
          </button>
        )}
        <div className="oc-emp-avatar">
          {emp.photo_url
            ? <img src={emp.photo_url} alt={emp.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : initials(emp.full_name)}
        </div>
        <div className="oc-title" style={{ marginTop: 6 }}>{emp.full_name}</div>
        {emp.role_title && <div className="oc-code">{emp.role_title}</div>}
        {otherRoles.length > 0 && (
          <div className="oc-multi-role-badge" title={otherRoles.map(r => r.role_title).join(', ')}>
            +{otherRoles.length} role{otherRoles.length !== 1 ? 's' : ''}
          </div>
        )}
        {direct.length > 0 && (
          <div className="oc-roles-badge" style={{ marginTop: 5 }}>
            {direct.length} report{direct.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {expanded && direct.length > 0 && (
        <>
          <div className="oc-connector-down" />
          <div className="oc-level">
            {direct.map((c) => (
              <EmployeeNode
                key={c.node_key || c.id}
                emp={c}
                onSelect={onSelect}
                selectedId={selectedId}
                onDragStart={onDragStart}
                onDrop={onDrop}
                canDrag={canDrag}
                onAddReport={onAddReport}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Employee sidebar panel ───────────────────────────────────────────────────

function EmployeeSidebar({ emp, onClose, onDragStart, canDrag }) {
  const direct = emp.direct_reports || [];

  return (
    <div className="oc-emp-sidebar">
      <div className="oc-emp-sidebar-header">
        <div className="oc-emp-sidebar-info">
          <div className="oc-emp-avatar oc-emp-avatar-lg">
            {emp.photo_url
              ? <img src={emp.photo_url} alt={emp.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : initials(emp.full_name)}
          </div>
          <div className="oc-emp-sidebar-textwrap">
            <div className="oc-emp-sidebar-name">{emp.full_name}</div>
            {emp.role_title && (
              <div className="oc-emp-sidebar-role">{emp.role_title}</div>
            )}
          </div>
        </div>
        <button className="oc-emp-sidebar-close" onClick={onClose}>✕</button>
      </div>

      <div className="oc-emp-sidebar-section">
        <div className="oc-emp-sidebar-label">
          Direct Reports <span className="oc-emp-count">{direct.length}</span>
        </div>

        {direct.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, padding: '10px 0' }}>
            No direct reports
          </div>
        ) : (
          <div className="oc-emp-report-list">
            {direct.map((r) => (
              <div
                key={r.id}
                className="oc-emp-report-item"
                draggable={canDrag}
                onDragStart={canDrag ? () => onDragStart(r.id, r.role_id) : undefined}
                title={canDrag ? 'Drag to reassign manager' : undefined}
                style={canDrag ? undefined : { cursor: 'default' }}
              >
                <div className="oc-emp-report-avatar">
                  {r.photo_url
                    ? <img src={r.photo_url} alt={r.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : initials(r.full_name)}
                </div>
                <div className="oc-emp-report-info">
                  <div className="oc-emp-report-name">{r.full_name}</div>
                  {r.role_title && (
                    <div className="oc-emp-report-role">{r.role_title}</div>
                  )}
                </div>
                {canDrag && <span className="oc-drag-handle" title="Drag to reassign">⠿</span>}
              </div>
            ))}
          </div>
        )}

        {canDrag && direct.length > 0 && (
          <div className="oc-emp-drag-hint">
            Drag a name and drop onto any employee card to reassign their manager
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add-report picker modal ──────────────────────────────────────────────────

function flattenEmployees(nodes, out = []) {
  for (const n of nodes || []) {
    out.push(n);
    flattenEmployees(n.direct_reports, out);
  }
  return out;
}

function buildParentMap(nodes, parentMap = new Map()) {
  for (const n of nodes || []) {
    for (const c of n.direct_reports || []) {
      parentMap.set(c.node_key || c.id, n);
    }
    buildParentMap(n.direct_reports, parentMap);
  }
  return parentMap;
}

// Only ancestors (+ self) are unsafe to add as a report — assigning your own
// manager, or your manager's manager, etc. would create a cycle. Descendants
// are fine: moving someone already under you to report to you directly
// (skipping a level) is a normal, valid reassignment.
function collectAncestorIds(managerNode, parentMap) {
  const out = new Set([managerNode.id]);
  let cur = managerNode;
  while (cur) {
    const parent = parentMap.get(cur.node_key || cur.id);
    if (!parent) break;
    out.add(parent.id);
    cur = parent;
  }
  return out;
}

function AddReportModal({ manager, empRoots, onAssign, onClose }) {
  const [query, setQuery] = useState('');
  const [assigning, setAssigning] = useState(null);

  const parentMap  = buildParentMap(empRoots);
  const excludeIds = collectAncestorIds(manager, parentMap);
  const directIds = new Set((manager.direct_reports || []).map(r => r.id));
  // Only people whose role actually reports to the manager's role in Role
  // Hierarchy are eligible — matches the server-side check in set-manager.
  const roleEligible = flattenEmployees(empRoots)
    .filter(e => e.reports_to_role_id === manager.role_id)
    .filter(e => !excludeIds.has(e.id) && !directIds.has(e.id));
  const candidates = roleEligible
    .filter(e => !query.trim() || e.full_name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Modal open title={`Add report to ${manager.full_name}`} onClose={onClose} size="sm">
      <div className="oc-modal-content">
        <input
          type="text"
          className="form-input"
          placeholder="Search employees…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          style={{ width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
        />
        {roleEligible.length === 0 ? (
          <p className="muted" style={{ padding: '12px 0' }}>
            No one holds a role that reports to {manager.role_title ? `"${manager.role_title}"` : 'this role'} in Role Hierarchy.
          </p>
        ) : candidates.length === 0 ? (
          <p className="muted" style={{ padding: '12px 0' }}>No matching employees.</p>
        ) : (
          <div className="oc-emp-report-list" style={{ maxHeight: 340, overflowY: 'auto' }}>
            {candidates.map((e) => (
              <div
                key={e.node_key || e.id}
                className="oc-emp-report-item"
                style={{ cursor: 'pointer', opacity: assigning === (e.node_key || e.id) ? 0.6 : 1 }}
                onClick={async () => {
                  if (assigning) return;
                  setAssigning(e.node_key || e.id);
                  try {
                    await onAssign(e.id, e.role_id);
                  } finally {
                    setAssigning(null);
                  }
                }}
              >
                <div className="oc-emp-report-avatar">
                  {e.photo_url
                    ? <img src={e.photo_url} alt={e.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : initials(e.full_name)}
                </div>
                <div className="oc-emp-report-info">
                  <div className="oc-emp-report-name">{e.full_name}</div>
                  {e.role_title && (
                    <div className="oc-emp-report-role">{e.role_title}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Orphan list ─────────────────────────────────────────────────────────────

function OrphanList({ title, items }) {
  return (
    <div style={{ marginTop: 8 }}>
      <strong>{title}:</strong>{' '}
      {items.map((s, i) => (
        <span key={i} className="org-employee-chip" style={{ marginLeft: 4 }}>{s}</span>
      ))}
    </div>
  );
}